import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { useNotificationContext } from "@/features/notifications/context/NotificationContext";
import { useStreamChat } from "@/features/chat/context/StreamChatContext";
import type { Module } from "@/config/roles";

interface NavItem {
  to: string;
  icon: string;
  labelKey: string;
  /** If set, canRead(requireModule) must return true for the item to appear */
  requireModule?: Module;
  /**
   * Company feature flag. For full-access users this check is skipped.
   * For restricted officers and employees the flag must be truthy.
   */
  companyFlag?: "company_ticketing" | "company_flight_tracker" | "company_form" | "task_enabled" | "bu_assessment_enabled";
  badge?: "chat" | "bell";
  /** If true, the item is hidden from non-officers (employees / USER type) */
  officerOnly?: boolean;
}

interface NavGroup {
  labelKey?: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    labelKey: "sidebar.section_feed",
    items: [
      { to: "/",              icon: "home",     labelKey: "home.title" },
      { to: "/announcements", icon: "megaphone", labelKey: "feed.management.title", requireModule: "announcements" },
      { to: "/files", icon: "folder", labelKey: "sharepoint.title", requireModule: "sharepoint" },
    ],
  },
  {
    labelKey: "sidebar.section_messaging",
    items: [
      { to: "/chat",  icon: "chat",     labelKey: "chat.add_message", badge: "chat" },
      { to: "/leave", icon: "calendar", labelKey: "leave.title",      requireModule: "leave",   companyFlag: "company_form" },
    ],
  },
  {
    labelKey: "sidebar.section_operations",
    items: [
      { to: "/inquiries",   icon: "ticket", labelKey: "inquiry.title",     requireModule: "inquiries", companyFlag: "company_ticketing" },
      { to: "/return-home", icon: "plane",  labelKey: "return_home.title", requireModule: "flight",    companyFlag: "company_flight_tracker" },
    ],
  },
  {
    labelKey: "sidebar.section_tasks",
    items: [
      { to: "/tasks", icon: "kanban", labelKey: "tasks.nav_title", officerOnly: true, companyFlag: "task_enabled" },
    ],
  },
  {
    labelKey: "sidebar.section_notifications",
    items: [
      { to: "/notifications", icon: "bell", labelKey: "notifications.title", badge: "bell" },
    ],
  },
  {
    labelKey: "sidebar.section_admin",
    items: [
      { to: "/users",         icon: "users",       labelKey: "user_management.nav_title",  requireModule: "profile",         officerOnly: true },
      { to: "/access/roles", icon: "shield",      labelKey: "access.roles.nav_title",     requireModule: "role_management", officerOnly: true },
      { to: "/other-setup",  icon: "sliders",     labelKey: "other_setup.title",          requireModule: "company",         officerOnly: true },
    ],
  },
  {
    labelKey: "sidebar.section_settings",
    items: [
      { to: "/settings", icon: "settings", labelKey: "settings.title" },
      { to: "/support",  icon: "lifebuoy", labelKey: "app_support.nav_title" },
    ],
  },
];

const iconMap: Record<string, string> = {
  users:    "M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75",
  building: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
  home:     "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1",
  megaphone:"M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z",
  ticket:   "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
  chat:     "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
  bell:     "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
  folder:   "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z",
  plane:    "M17.8 19.2L16 11l3.5-3.5C20.3 6.7 20.3 5.3 19.5 4.5 18.7 3.7 17.3 3.7 16.5 4.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5L2.8 8.3c-.3.5-.1 1.1.4 1.3L9 12l-2 2H4l-1 1 3 2 2 3 1-1v-3l2-2 2.4 5.8c.2.5.8.7 1.3.4l1.6-.9c.4-.2.6-.6.5-1.1z",
  calendar: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  settings: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
  shield:   "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  send:     "M12 19l9 2-9-18-9 18 9-2zm0 0v-8",
  tag:      "M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z",
  card:     "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
  sliders:  "M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4",
  kanban:    "M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2",
  clipboard: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01",
  lifebuoy:  "M18.364 5.636a9 9 0 010 12.728M5.636 18.364a9 9 0 010-12.728M12 2v2m0 16v2M2 12h2m16 0h2m-4.343-5.657l-1.414 1.414M7.757 16.243l-1.414 1.414m9.9 0l-1.414-1.414M7.757 7.757L6.343 6.343M12 9a3 3 0 100 6 3 3 0 000-6z",
};

export const Sidebar = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const { canRead, isOfficer, isFullAccess } = usePermissions();
  const { unreadCount } = useNotificationContext();
  const { unreadChatCount } = useStreamChat();

  const getBadgeCount = (badge?: "chat" | "bell") => {
    if (badge === "chat") return unreadChatCount;
    if (badge === "bell") return unreadCount;
    return 0;
  };

  return (
    <aside className="hidden md:flex md:flex-col w-64 bg-white border-r border-gray-200">
      {/* Brand */}
      <div className="p-6 border-b border-gray-200">
        <h1 className="text-xl font-bold text-indigo-600">HoRenSo</h1>
        <p className="text-xs text-gray-500 mt-1">Consult | Connect | Communicate</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {navGroups.map((group, groupIdx) => {
          const visibleItems = group.items.filter((item) => {
            // Officer-only items are hidden from employees regardless of other checks
            if (item.officerOnly && !isOfficer) return false;
            // Module-gated items: check canRead (handles full-access, restricted officers,
            // and employees — employees always return true from canRead)
            if (item.to === "/" && userData?.userType?.toLowerCase() === "souser" && !canRead("announcements")) return false;
            if (item.requireModule && !canRead(item.requireModule)) return false;
            // Company feature flag: full-access users always see; everyone else needs the flag
            if (item.companyFlag && !isFullAccess && !userData?.[item.companyFlag]) return false;
            return true;
          });
          if (visibleItems.length === 0) return null;

          return (
            <div key={groupIdx}>
              {groupIdx > 0 && (
                <div className="my-2 mx-2 border-t border-gray-100" />
              )}

              {group.labelKey && (
                <p className="px-3 pt-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {t(group.labelKey)}
                </p>
              )}

              <div className="space-y-0.5">
                {visibleItems.map((item) => {
                  const badgeCount = getBadgeCount(item.badge);

                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-indigo-50 text-indigo-700"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`
                      }
                    >
                      <svg
                        className="w-[18px] h-[18px] flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d={iconMap[item.icon]}
                        />
                      </svg>
                      <span className="truncate">{t(item.labelKey)}</span>
                      {badgeCount > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-[10px] font-semibold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                          {badgeCount > 99 ? "99+" : badgeCount}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Brand footer */}
      <div className="p-4 border-t border-gray-100">
        <span className="text-xs font-light tracking-widest text-gray-300 uppercase select-none">
          HoRenSo <span className="text-gray-200">2026</span>
        </span>
      </div>

    </aside>
  );
};
